import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { FieldTooltip } from '../../workflows/FieldHeader';
import { ReportCard } from '../ReportCard';
import type { PerformanceReport } from './data/api';
import { percent, ratio } from './data/format';

const OVER_INDEX_TOOLTIP =
  'Share of winners ÷ competitor usage. Above 1× your winning creatives use this tag more than competitors do; 1× is the same rate; below 1× it is less.';

function ShareBar({ share, color }: { share: number | null; color: string }): ReactNode {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <Box
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 999,
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${Math.min(100, (share ?? 0) * 100)}%`,
            height: '100%',
            borderRadius: 999,
            bgcolor: color,
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', width: 44, textAlign: 'right' }}>
        {percent(share, 1)}
      </Typography>
    </Box>
  );
}

export function CreativeDna({ report }: { report: PerformanceReport }): ReactNode {
  if (report.dna.length === 0) {
    return (
      <ReportCard>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          No winning creatives carried a shared element this period.
        </Typography>
      </ReportCard>
    );
  }

  const benchmarked = report.dna.some((tag) => tag.competitorShare !== null);

  return (
    <ReportCard>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Feature</TableCell>
            <TableCell>Tag</TableCell>
            <TableCell sx={{ width: 260 }}>Share of winners</TableCell>
            {benchmarked && <TableCell sx={{ width: 260 }}>Competitor usage</TableCell>}
            {benchmarked && (
              <TableCell align="right">
                <Box
                  sx={{
                    display: 'inline-flex',
                    gap: 0.5,
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  Over-index
                  <FieldTooltip tooltip={OVER_INDEX_TOOLTIP} />
                </Box>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {report.dna.map((tag) => (
            <TableRow key={`${tag.feature}-${tag.tag}`}>
              <TableCell sx={{ color: 'text.secondary' }}>{tag.feature}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{tag.tag}</TableCell>
              <TableCell>
                <ShareBar share={tag.winnerShare} color="primary.main" />
              </TableCell>
              {benchmarked && (
                <TableCell>
                  <ShareBar share={tag.competitorShare} color="text.disabled" />
                </TableCell>
              )}
              {benchmarked && (
                <TableCell align="right" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                  {ratio(tag.winnerShare, tag.competitorShare)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!benchmarked && (
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          This scope has no competitor integrations, so there is nothing to benchmark against.
        </Typography>
      )}
    </ReportCard>
  );
}
